import React, { useCallback, useEffect, useState } from 'react'
import className from 'classnames/bind'
import InfiniteScroll from 'react-infinite-scroller'
import { useHistory } from 'react-router'

import style from './MyhouseNoticeListPage.module.scss'

import BlindContent from '@Components/atoms/BlindContent'
import MyhouseNoticeList from '@Components/modules/MyhouseNoticeList'
import MyhouseNoticeListHeader from '@Components/atoms/MyhouseNoticeListHeader'
import { MY_HOUSE_AUTH_STATUS, MY_HOUSE_NOTICE_CATEGORY, MY_HOUSE_NOTICE_CATEGORY_OBJ } from '@Root/contants/myhouse'
import useMyhouse from '@Hooks/myhouse/useMyhouse'
import MyhouseNoticeListCategory from '@Components/atoms/MyhouseNoticeListCategory'
import useNotice from '@Hooks/myhouse/notice/useNotice'
import useBoard from '@Hooks/myhouse/board/useBoard'
import MyHouseNoticeSummaryResponse from '@Types/entities/MyHouseNoticeSummaryResponse'
import { LAND_SERVICE_PREFIX_URL } from '@Root/contants/urlConstants'
import useModal from '@Hooks/myhouse/useModal'
import { MODAL_TYPE } from '@Root/contants/myhouseModal'
import { FETCH_STATUS } from '@Root/contants'
import MyhouseCommonProgress from '@Components/atoms/MyhouseCommonProgress'
import { lcsSend } from '@Helpers/lcs'
import { NDS_PROTOCOL } from '@Root/contants/lcs'
import { useSessionStorage } from '@Root/utils/storageUtils'

const Notice: React.FC = () => {
  //변수선언부
  const { myhouse } = useMyhouse()
  const { complexNo, authComplexNoMap } = myhouse
  const { notice, loadInitialNoticeList, loadNoticeList, loadAttentionNoticeList } = useNotice()
  const { isRecent, isVisited, getSimplifiedDate, getAuthStatusForBoard } = useBoard()
  const { noticeList, attentionList, hasMore, currentCategory, isShowTabLoading } = notice
  const { fetchStatus: noticeListFetchStatus } = noticeList
  const history = useHistory()
  const { open } = useModal()
  const [isShowLoading, setIsShowLoading] = useState(true)
  const { getStorage, setStorage } = useSessionStorage()

  const currentListLength = noticeList[currentCategory]?.length

  const backMyhouseNo = getStorage(['bbs', 'complex', 'house', complexNo]) || authComplexNoMap?.[complexNo]?.[0]?.houseNo || ''

  const headerProps = {
    prev: {
      href: `${LAND_SERVICE_PREFIX_URL.MYHOUSE}/${backMyhouseNo}`,
      text: '이전 페이지로 돌아가기',
    },
    title: '공지사항',
    enrollment: {
      onClick: (e: React.SyntheticEvent) => {
        e.preventDefault()

        if (getAuthStatusForBoard() === MY_HOUSE_AUTH_STATUS.COMPLETE) {
          history.push(`${LAND_SERVICE_PREFIX_URL.MYHOUSE_COMPLEXES}/${complexNo}/board/form/notices`)
          return
        } else if (getAuthStatusForBoard() === MY_HOUSE_AUTH_STATUS.PROGRESS) {
          open(MODAL_TYPE.AUTH_NOT_COMPLETED)
        } else {
          open(MODAL_TYPE.MYHOUSE_AUTHENTICATION_REQUIRED, { complexNo: complexNo })
        }
      },
      text: '등록하기',
    },
  }

  const currentHasMore = hasMore[currentCategory]

  const emptyState = {
    text: '등록된 공지사항이 없습니다.',
    isHidden: !!noticeList[currentCategory]?.length,
  }

  //함수정의부
  const scrollHandler = useCallback(() => {
    if (complexNo && currentListLength && noticeListFetchStatus === FETCH_STATUS.SUCCESS) {
      loadNoticeList({
        complexNo,
        offset: noticeList[currentCategory].length,
        limit: 20,
        category: currentCategory,
      })
    }
  }, [currentCategory, currentListLength, noticeListFetchStatus])

  const noticeListMapCallback = ({ sequence, readCount, modifiedDate, title, isAttention, category, imageUrl }: MyHouseNoticeSummaryResponse) => {
    return {
      sequence,
      recent: isRecent(modifiedDate),
      visited: isVisited(sequence),
      target: MY_HOUSE_NOTICE_CATEGORY_OBJ[category as MY_HOUSE_NOTICE_CATEGORY].SUMMARY_LABEL,
      title,
      attentionText: isAttention ? '필독' : null,
      href: `/myhouse/complexes/${complexNo}/notices/${sequence}`,
      moreData: {
        date: getSimplifiedDate(modifiedDate),
        view: readCount,
      },
      imageUrl,
    }
  }

  const noticeListProps = {
    emptyState,
    attentionList: attentionList[currentCategory]?.map(noticeListMapCallback),
    itemList: noticeList[currentCategory]?.map(noticeListMapCallback),
  }

  //useEffect
  useEffect(() => {
    lcsSend(NDS_PROTOCOL.MY_HOUSE.NOTICE.LIST)
  }, [])

  useEffect(() => {
    if (complexNo) {
      if (
        [MY_HOUSE_NOTICE_CATEGORY.ALL, MY_HOUSE_NOTICE_CATEGORY.OFFICE, MY_HOUSE_NOTICE_CATEGORY.MEETING, MY_HOUSE_NOTICE_CATEGORY.GOVERNMENT].includes(
          currentCategory as MY_HOUSE_NOTICE_CATEGORY,
        )
      ) {
        loadAttentionNoticeList({ complexNo, category: currentCategory, limit: 5 })
      }

      loadInitialNoticeList({ complexNo, category: currentCategory, offset: 0, limit: 20 })
    }
  }, [complexNo, currentCategory])

  useEffect(() => {
    if (noticeListFetchStatus === FETCH_STATUS.SUCCESS) {
      setIsShowLoading(false)
    }
  }, [noticeListFetchStatus])

  if (isShowLoading) {
    return <MyhouseCommonProgress isViewportHeight />
  }

  const cx = className.bind(style)

  return (
    <>
      <MyhouseNoticeListHeader {...headerProps} />
      <section id="content" className={cx('article')} role="main">
        <BlindContent />
        <div className={cx('inner')}>
          <MyhouseNoticeListCategory />
          {isShowTabLoading && <MyhouseCommonProgress />}
          {!isShowTabLoading && (
            <InfiniteScroll loadMore={scrollHandler} hasMore={currentHasMore} loader={<div className="loader" key={0}></div>} threshold={100}>
              <MyhouseNoticeList {...noticeListProps} />
            </InfiniteScroll>
          )}
        </div>
      </section>
    </>
  )
}

export default Notice
